import React, { useMemo, useRef, useState } from "react";
import { ReactComponent as CalenderIcon } from "../../../assets/icons/calender.svg";
import { ReactComponent as InformationIcon } from "../../../assets/icons/information.svg";
import { ReactComponent as ViewIcon } from "../../../assets/icons/view.svg";
import IconButton from "../Buttons/IconButton";
import Tooltip from "../Tooltip/Tooltip";
import "../../../css/DesignSystem/Inputs/Field.css";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const supportsNativeMonthInput = () => {
  const input = document.createElement("input");
  input.setAttribute("type", "month");
  input.value = "2026-06";
  return input.type === "month" && input.value === "2026-06";
};

const Field = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder = "",
  full = false,
  options = [],
  readOnly = false,
  disabled = false,
  min,
  step,
  maxLength,
  pattern,
  inputMode,
  tooltip,
  showPasswordToggle = false,
}) => {
  const inputRef = useRef(null);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const showCustomPickerIcon = type === "date" || type === "month";
  const hasPasswordToggle = type === "password" && showPasswordToggle;
  const inputType = hasPasswordToggle && passwordVisible ? "text" : type;

  const nativeMonthSupported = useMemo(() => {
    if (typeof document === "undefined") return false;
    return supportsNativeMonthInput();
  }, []);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 80 }, (_, index) => currentYear - 40 + index);
  }, []);

  const valueParts = String(value ?? "").split("-");
  const selectedYear = valueParts[0] || "";
  const selectedMonth = valueParts[1] || "";

  const handleOpenPicker = () => {
    if (readOnly || disabled) return;

    const input = inputRef.current;
    if (!input) return;

    if (type === "month" && !nativeMonthSupported) {
      setMonthPickerOpen((prev) => !prev);
      return;
    }

    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch {
        // fallback below
      }
    }

    input.focus();
  };

  const handleMonthChange = (nextMonth) => {
    const year = selectedYear || String(new Date().getFullYear());
    onChange(`${year}-${nextMonth}`);
  };

  const handleYearChange = (nextYear) => {
    const month = selectedMonth || "01";
    onChange(`${nextYear}-${month}`);
  };

  return (
    <div className={`smField ${full ? "smFieldFull" : ""}`}>
      <label className="FP_Content_Semibold smFieldLabel">
        <span>{label}</span>
        {tooltip ? (
          <Tooltip content={tooltip} position="top">
            <InformationIcon
              className="smFieldTooltipIcon"
              aria-label={`${label} help`}
            />
          </Tooltip>
        ) : null}
      </label>

      {type === "select" ? (
        <select
          className="smFieldInput FP_Content"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select</option>
          {options.map((opt) => (
            <option key={opt.value || opt} value={opt.value || opt}>
              {opt.label || opt}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          className="smFieldInput smFieldTextarea FP_Content"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
        />
      ) : (
        <div className="smFieldInputWrap">
          <input
            ref={inputRef}
            className={`smFieldInput FP_Content${hasPasswordToggle ? " smFieldInputWithAction" : ""}`}
            type={inputType}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            readOnly={readOnly || (type === "month" && !nativeMonthSupported)}
            disabled={disabled}
            min={min}
            step={step}
            maxLength={maxLength}
            pattern={pattern}
            inputMode={inputMode}
          />

          {hasPasswordToggle ? (
            <IconButton
              className={`smFieldPasswordToggle${passwordVisible ? " is-visible" : ""}`}
              icon={<ViewIcon aria-hidden />}
              label={passwordVisible ? "Hide password" : "Show password"}
              onClick={() => setPasswordVisible((visible) => !visible)}
              disabled={disabled}
            />
          ) : null}

          {showCustomPickerIcon && (
            <button
              type="button"
              className="smFieldCalendarButton"
              onClick={handleOpenPicker}
              disabled={disabled || readOnly}
              aria-label={`Open ${label} picker`}
            >
              <CalenderIcon />
            </button>
          )}

          {type === "month" && !nativeMonthSupported && monthPickerOpen && (
            <div className="smMonthPicker">
              <select
                className="smMonthPickerSelect FP_Content"
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                <option value="">Month</option>
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>

              <select
                className="smMonthPickerSelect FP_Content"
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
              >
                <option value="">Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Field;
